import { useLocalizedText } from './localizedText';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';

/**
 * Default width (px) for the Shift Name column, shared by the volunteer and coordinator shift tables.
 * Applied as both `width` (the initial column width) and `minWidth` (the resize floor). Both tables
 * enable `resizableColumns`, so the table uses a fixed layout where this is the starting width and the
 * user can drag the column wider to read a long name; the matching `minWidth` keeps it from being
 * dragged below this readable default — the floor cce-8012 established to stop long names pushing the
 * other columns off screen.
 */
export const SHIFT_NAME_COLUMN_WIDTH_PX = 150;

export type ColumnMap<T> = {
  [key: string]: (item: T) => JSX.Element | string;
};

export type ColumnData<T> = {
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  columnDisplay: TableProps.ColumnDisplayProperties[];
};

export type EditableColConfig<T> = TableProps.ColumnDefinition<T>['editConfig'];

export function useTableColumns<T>(
  columnMap: ColumnMap<T>,
  tableName: string,
  namespaces: string[] = ['translation'],
  editableCols: Record<string, EditableColConfig<T>> | undefined = {},
  columnWidths: Record<string, number> = {}
): ColumnData<T> {
  const { t } = useLocalizedText(namespaces);

  return {
    columnDisplay: Object.keys(columnMap).map((key) => ({
      id: key,
      visible: true,
    })),
    columnDefinitions: Object.entries(columnMap).map((entry) => {
      const [key, mapValue] = entry;
      return {
        id: key,
        header: t(`${tableName}.columns.${key}`),
        editConfig:
          editableCols && Object.keys(editableCols).includes(key) ? editableCols[key] : undefined,
        width: columnWidths[key],
        minWidth: columnWidths[key],
        // This cast is required because Object.entries converts the value to unknown, even though it is defined in the ColumnMap type
        cell: mapValue as (item: T) => JSX.Element | string,
      };
    }),
  };
}
